import React, { useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { blogPosts } from '../data/blogData';
import AnimatedText from './AnimatedText';

const CATEGORIES = ['All', 'Marketing Trends', 'Brand Building', 'Social Media', 'Content Strategy', 'Influencer Marketing', 'Web Design'];

const formatDate = (d) =>
  new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const Blog = () => {
  const [category, setCategory] = useState('All');
  const [hovered, setHovered] = useState(null);
  const containerRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { damping: 22, stiffness: 220, mass: 0.4 });
  const sy = useSpring(my, { damping: 22, stiffness: 220, mass: 0.4 });

  const posts = useMemo(
    () => (category === 'All' ? blogPosts : blogPosts.filter((p) => p.category === category)),
    [category]
  );

  const onMove = (e) => {
    if (!containerRef.current) return;
    const r = containerRef.current.getBoundingClientRect();
    mx.set(e.clientX - r.left);
    my.set(e.clientY - r.top);
  };

  return (
    <section
      id="blog"
      data-testid="blog-section"
      className="py-24 sm:py-32 px-6 sm:px-8 lg:px-12 bg-[#F4F4F3]"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 sm:mb-16">
          <p className="lg:col-span-4 text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#AA94FF]">
            Journal
          </p>
          <div className="lg:col-span-8">
            <AnimatedText
              as="h2"
              className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-[-0.03em] leading-[0.98] text-[#1C1917]"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              y={60}
              stagger={0.05}
            >
              Ideas, notes,
            </AnimatedText>
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="block text-4xl sm:text-6xl lg:text-7xl italic font-light tracking-[-0.02em] leading-[0.98] text-[#AA94FF] mt-2"
              style={{ fontFamily: "'Cabinet Grotesk', serif" }}
            >
              what's working now.
            </motion.span>
          </div>
        </div>

        {/* Category chips */}
        <div className="flex flex-wrap gap-2 mb-10">
          {CATEGORIES.map((c) => {
            const active = c === category;
            return (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-full border transition-all ${
                  active
                    ? 'bg-[#1C1917] text-[#F4F4F3] border-[#1C1917]'
                    : 'bg-transparent text-[#1C1917] border-[#1C1917]/20 hover:border-[#1C1917]'
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>

        {/* Hover-preview article list */}
        <div
          ref={containerRef}
          onMouseMove={onMove}
          onMouseLeave={() => setHovered(null)}
          className="relative border-t border-[#1C1917]/15"
        >
          {posts.map((p, i) => (
            <motion.div
              key={p.id}
              data-testid={`blog-row-${p.id}`}
              onMouseEnter={() => setHovered(p.id)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="relative border-b border-[#1C1917]/15"
            >
              <Link
                to={`/blog/${p.slug}`}
                data-cursor-label="Read"
                className="group flex items-center justify-between gap-6 py-7 sm:py-10 hover:pl-6 transition-all duration-500"
              >
                <motion.span
                  aria-hidden="true"
                  className="absolute left-0 top-0 bottom-0 bg-[#AA94FF]/8 pointer-events-none"
                  animate={{ width: hovered === p.id ? '100%' : '0%' }}
                  transition={{ duration: 0.5, ease: [0.2, 0.65, 0.3, 0.9] }}
                />

                <div className="relative flex items-baseline gap-4 sm:gap-8 min-w-0">
                  <span
                    className="text-sm text-[#78716C] w-10 shrink-0 font-mono"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    0{i + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#AA94FF] font-bold mb-1">
                      {p.category}
                    </p>
                    <h3
                      className={`text-xl sm:text-3xl lg:text-4xl font-black tracking-[-0.02em] leading-[1.05] transition-colors duration-500 ${
                        hovered === p.id ? 'text-[#AA94FF]' : 'text-[#1C1917]'
                      }`}
                      style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                    >
                      {p.title}
                    </h3>
                  </div>
                </div>

                <div className="relative flex items-center gap-4 sm:gap-6 shrink-0">
                  <span className="hidden md:inline text-xs uppercase tracking-[0.15em] text-[#78716C]">
                    {formatDate(p.date)}
                  </span>
                  <span className="hidden md:inline text-xs uppercase tracking-[0.15em] text-[#78716C]">
                    {p.readTime}
                  </span>
                  <motion.span
                    animate={{
                      rotate: hovered === p.id ? 0 : -45,
                      scale: hovered === p.id ? 1.1 : 1,
                    }}
                    transition={{ type: 'spring', damping: 14, stiffness: 220 }}
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      hovered === p.id ? 'bg-[#AA94FF] text-[#1C1917]' : 'text-[#1C1917]'
                    }`}
                  >
                    <ArrowUpRight className="w-5 h-5" />
                  </motion.span>
                </div>
              </Link>
            </motion.div>
          ))}

          {/* Cursor-following image preview (desktop only) */}
          <AnimatePresence>
            {hovered && (
              <motion.div
                key={hovered}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
                transition={{ duration: 0.25 }}
                style={{
                  x: sx,
                  y: sy,
                  translateX: '-50%',
                  translateY: '-50%',
                }}
                className="pointer-events-none absolute top-0 left-0 w-[280px] h-[340px] sm:w-[360px] sm:h-[420px] rounded-2xl overflow-hidden shadow-2xl z-10 hidden md:block"
              >
                <img
                  src={posts.find((x) => x.id === hovered)?.image}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/40 via-transparent to-transparent" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {posts.length === 0 && (
          <p className="text-center text-[#78716C] py-16">No articles in this category yet.</p>
        )}
      </div>
    </section>
  );
};

export default Blog;
